// ============================================================================
// FSA TEMPLATE SWITCH MODAL (ADR-0004 Phase 2 + any-template switch amendment)
// ============================================================================
// Auditor-initiated move of ONE engagement onto a newer version of its
// template or onto any other installed template. Pick a target → the modal
// loads a code-mapped preview: what happens to every classified ledger (kept /
// schedule-only / unmapped / restored from a previous time on that template),
// notes whose data would no longer show, statements to regenerate, reports
// whose wording changes, and — across corporate ↔ non-corporate — signatories
// to re-pick. Applies transactionally after an explicit confirm.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import { fsaTemplateUpgradeService } from 'api/services/fsa/fsaTemplateUpgradeService';
import type { ID } from 'api/types';
import type {
  TemplateSwitchTarget,
  TemplateUpgradeItem,
  TemplateUpgradePreview,
} from 'api/types/fsa/templateUpgrade.types';

interface Props {
  isOpen: boolean;
  engagementId: ID;
  templateName?: string | null;
  onClose: () => void;
  /** Fired after a successful switch — parent refetches settings/TB/notes/statements. */
  onUpgraded: () => void;
}

const sectionTitleStyle: React.CSSProperties = {
  fontSize: '0.8rem', fontWeight: 600, margin: '10px 0 4px', color: 'var(--text-primary)',
  display: 'flex', alignItems: 'center', gap: 6,
};

const chipListStyle: React.CSSProperties = {
  display: 'flex', flexWrap: 'wrap', gap: 6, maxHeight: 110, overflowY: 'auto',
};

const chipStyle = (bg: string, fg: string): React.CSSProperties => ({
  background: bg, color: fg, borderRadius: 10, padding: '2px 8px',
  fontSize: '0.72rem', fontWeight: 500, whiteSpace: 'nowrap',
});

const calloutStyle = (bg: string, fg: string): React.CSSProperties => ({
  marginTop: 10, padding: '0.6rem 0.75rem', borderRadius: 6, background: bg, color: fg,
  fontSize: '0.8rem', display: 'flex', flexDirection: 'column', gap: 4,
});

const statStyle = (bg: string, fg: string): React.CSSProperties => ({
  flex: 1, minWidth: 110, padding: '0.5rem 0.6rem', borderRadius: 6, background: bg, color: fg,
  display: 'flex', flexDirection: 'column', gap: 2,
});

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

const ChipSection: React.FC<{ title: string; items: TemplateUpgradeItem[]; bg: string; fg: string }> =
  ({ title, items, bg, fg }) => {
    if (items.length === 0) return null;
    return (
      <div>
        <div style={sectionTitleStyle}>{title} <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>({items.length})</span></div>
        <div style={chipListStyle}>
          {items.map(i => (
            <span key={i.code} style={chipStyle(bg, fg)} title={i.name ?? i.code}>{i.name || i.code}</span>
          ))}
        </div>
      </div>
    );
  };

const targetLabel = (t: TemplateSwitchTarget) =>
  `${t.name} · ${t.code} v${t.version}${t.isCurrent ? ' (current)' : ''}`;

const FsaTemplateUpgradeModal: React.FC<Props> = ({ isOpen, engagementId, templateName, onClose, onUpgraded }) => {
  const [targets, setTargets] = useState<TemplateSwitchTarget[]>([]);
  const [targetId, setTargetId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<TemplateUpgradePreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [upgrading, setUpgrading] = useState(false);

  // Load the targets on open. Default to a newer version of the engagement's
  // own template when one is installed — the common case — otherwise wait
  // for the auditor to pick.
  useEffect(() => {
    if (!isOpen) return;
    setTargets([]);
    setTargetId('');
    setPreview(null);
    setError(null);
    fsaTemplateUpgradeService.targets(engagementId)
      .then(list => {
        setTargets(list);
        const current = list.find(t => t.isCurrent);
        const newer = list.find(t => t.isSameCode && !t.isCurrent && current != null && t.version > current.version);
        if (newer) setTargetId(newer.templateId);
      })
      .catch((err: any) => setError(err?.response?.data?.message || err?.message || 'Could not load the templates.'));
  }, [isOpen, engagementId]);

  useEffect(() => {
    if (!isOpen || !targetId) { setPreview(null); return; }
    let cancelled = false;
    setPreview(null);
    setError(null);
    setLoading(true);
    fsaTemplateUpgradeService.preview(engagementId, targetId)
      .then(p => { if (!cancelled) setPreview(p); })
      .catch((err: any) => { if (!cancelled) setError(err?.response?.data?.message || err?.message || 'Could not load the preview.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen, engagementId, targetId]);

  const targetOptions = useMemo(() => targets.map(t => ({
    value: t.templateId,
    label: targetLabel(t),
    isDisabled: t.isCurrent,
  })), [targets]);

  const currentTarget = targets.find(t => t.isCurrent);

  const handleConfirmUpgrade = useCallback(async () => {
    if (!preview) return;
    setUpgrading(true);
    try {
      const result = await fsaTemplateUpgradeService.upgrade(engagementId, preview.toTemplateId);
      setUpgrading(false);
      setConfirmOpen(false);
      const moved = result.tbEntriesToScheduleOnly + result.tbEntriesUnclassified;
      notificationService.toast.success(
        `Now on ${result.toTemplateName || result.toTemplateCode} v${result.toVersion} — ${result.notesRekeyed} notes carried over` +
        (moved > 0 ? `; ${plural(moved, 'ledger')} need mapping` : '') +
        (result.tbEntriesRestored > 0 ? `; ${plural(result.tbEntriesRestored, 'ledger')} restored` : '') + '.',
      );
      onUpgraded();
      onClose();
    } catch (err: any) {
      setUpgrading(false);
      setConfirmOpen(false);
      const data = err?.response?.data;
      notificationService.toast.error(
        data?.message
          ?? (Array.isArray(data?.errors) && data.errors.length ? data.errors.join('; ') : null)
          ?? err?.message ?? 'Template switch failed.',
      );
    }
  }, [engagementId, preview, onUpgraded, onClose]);

  if (!isOpen) return null;

  const ready = !!preview && !preview.blocked && !preview.alreadyOnTarget && !loading;
  const canUpgrade = ready && !upgrading;
  const isSwitch = !!preview?.isTemplateSwitch;
  const degraded = preview ? preview.tbEntriesToScheduleOnly + preview.tbEntriesToUnclassify : 0;
  const hasStructuralChanges = !!preview && (
    preview.schedulesAdded.length + preview.schedulesRemoved.length +
    preview.classesAdded.length + preview.classesRemoved.length > 0);
  const hasWarnings = !!preview && (
    degraded > 0 || preview.orphanedNotes.length > 0 || preview.orphanedReports.length > 0 ||
    preview.crossesEntityFamily);
  const toLabel = preview
    ? (isSwitch ? `${preview.toTemplateName} v${preview.toVersion}` : `v${preview.toVersion}`)
    : '';

  return (
    <>
      <div className="modal-backdrop" onClick={upgrading ? undefined : onClose} />
      <div className="modal-container modal-lg">
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Switch Template</h2>
          <button className="modal-close" onClick={onClose} disabled={upgrading}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content">
          <div className="form-group">
            <SyutSelect
              label="Move this engagement to"
              value={targetId}
              onChange={(v) => setTargetId((v as string) || '')}
              options={targetOptions}
              isSearchable
              placeholder="Choose a template…"
            />
            <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginTop: 4 }}>
              Currently on <strong>{currentTarget ? targetLabel(currentTarget).replace(' (current)', '') : (templateName ?? '—')}</strong>.
              Any installed template can be chosen — saved data is carried across by note and class code.
            </div>
          </div>

          {loading && (
            <div style={{ padding: '1rem 0', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
              Comparing templates…
            </div>
          )}

          {error && (
            <div style={{ padding: '0.75rem', borderRadius: 6, background: '#fee2e2', color: '#991b1b', fontSize: '0.82rem' }}>
              {error}
            </div>
          )}

          {preview && !loading && (
            <>
              {/* From → to banner */}
              <div style={{
                display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10, padding: '0.6rem 0.75rem',
                borderRadius: 6, background: 'var(--bg-secondary)', marginBottom: 4,
              }}>
                <span style={chipStyle('#e0e7ff', '#3730a3')}>{preview.fromTemplateName} v{preview.fromVersion}</span>
                <IconSystem.arrowRight size={14} />
                <span style={chipStyle('#dcfce7', '#166534')}>{preview.toTemplateName} v{preview.toVersion}</span>
                {isSwitch && <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>different template</span>}
              </div>

              {preview.alreadyOnTarget && (
                <div style={{ padding: '0.75rem', borderRadius: 6, background: '#dcfce7', color: '#166534', fontSize: '0.82rem' }}>
                  This engagement is already on this template — nothing to change.
                </div>
              )}

              {preview.blocked && (
                <div style={{ padding: '0.75rem', borderRadius: 6, background: '#fee2e2', color: '#991b1b', fontSize: '0.82rem' }}>
                  {preview.blockReason ?? 'This engagement cannot be moved.'}
                </div>
              )}

              {!preview.alreadyOnTarget && !preview.blocked && (
                <>
                  {preview.crossesEntityFamily && (
                    <div style={calloutStyle('#fee2e2', '#991b1b')}>
                      <strong style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <IconSystem.alert size={14} />
                        {preview.fromEntityType === 'COMPANY' ? 'Corporate → non-corporate' : 'Non-corporate → corporate'} switch
                      </strong>
                      {preview.signatoriesToRepick > 0 && (
                        <span>
                          {plural(preview.signatoriesToRepick, 'entity-side signatory', 'entity-side signatories')} on the
                          signature panel must be re-picked from the {preview.fromEntityType === 'COMPANY' ? 'Partners' : 'Directors'} master.
                        </span>
                      )}
                      {preview.notices.map(n => <span key={n}>{n}</span>)}
                    </div>
                  )}

                  {/* Ledger outcomes */}
                  <div style={sectionTitleStyle}>Trial balance ledgers</div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <div style={statStyle('#dcfce7', '#166534')}>
                      <strong style={{ fontSize: '1rem' }}>{preview.tbEntriesKept}</strong>
                      <span style={{ fontSize: '0.72rem' }}>keep their mapping</span>
                    </div>
                    <div style={statStyle('#fef3c7', '#92400e')}>
                      <strong style={{ fontSize: '1rem' }}>{preview.tbEntriesToScheduleOnly}</strong>
                      <span style={{ fontSize: '0.72rem' }}>move to schedule-only</span>
                    </div>
                    <div style={statStyle('#fee2e2', '#991b1b')}>
                      <strong style={{ fontSize: '1rem' }}>{preview.tbEntriesToUnclassify}</strong>
                      <span style={{ fontSize: '0.72rem' }}>become unmapped</span>
                    </div>
                    {preview.tbEntriesRestorable > 0 && (
                      <div style={statStyle('#dbeafe', '#1e40af')}>
                        <strong style={{ fontSize: '1rem' }}>{preview.tbEntriesRestorable}</strong>
                        <span style={{ fontSize: '0.72rem' }}>restored from before</span>
                      </div>
                    )}
                  </div>

                  {preview.affectedLedgers.length > 0 && (
                    <div style={degraded > 0 ? calloutStyle('#fef3c7', '#92400e') : calloutStyle('#dbeafe', '#1e40af')}>
                      <span>
                        {preview.tbEntriesToScheduleOnly > 0 && (
                          <>{plural(preview.tbEntriesToScheduleOnly, 'ledger')} will move to <strong>schedule-only</strong> — the note
                            exists in the new template but the class does not, so pick a class for {preview.tbEntriesToScheduleOnly === 1 ? 'it' : 'them'}. </>
                        )}
                        {preview.tbEntriesToUnclassify > 0 && (
                          <>{plural(preview.tbEntriesToUnclassify, 'ledger')} will become <strong>unmapped</strong> — the note does not
                            exist in the new template, so {preview.tbEntriesToUnclassify === 1 ? 'it returns' : 'they return'} to the mapping queue. </>
                        )}
                        {preview.tbEntriesRestorable > 0 && (
                          <>{plural(preview.tbEntriesRestorable, 'ledger')} get their mapping back from the last time this
                            engagement was on {preview.toTemplateName}.</>
                        )}
                      </span>
                      <details>
                        <summary style={{ cursor: 'pointer', fontWeight: 600 }}>Show the {plural(preview.affectedLedgers.length, 'ledger')}</summary>
                        <div style={{ maxHeight: 220, overflowY: 'auto', marginTop: 6, background: 'var(--bg-primary)', borderRadius: 4 }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.74rem', color: 'var(--text-primary)' }}>
                            <thead>
                              <tr style={{ textAlign: 'left', background: 'var(--bg-secondary)' }}>
                                <th style={{ padding: '4px 6px' }}>Ledger</th>
                                <th style={{ padding: '4px 6px' }}>Mapped to now</th>
                                <th style={{ padding: '4px 6px' }}>After the switch</th>
                              </tr>
                            </thead>
                            <tbody>
                              {preview.affectedLedgers.map((l, i) => (
                                <tr key={`${l.trialBalanceName}-${l.ledgerName}-${i}`} style={{ borderTop: '1px solid var(--border-color)' }}>
                                  <td style={{ padding: '4px 6px' }}>
                                    {l.ledgerName}
                                    {l.trialBalanceName && (
                                      <span style={{ color: 'var(--text-secondary)' }}> · {l.trialBalanceName}</span>
                                    )}
                                  </td>
                                  <td style={{ padding: '4px 6px' }}>
                                    {l.fromScheduleCode
                                      ? <>
                                          {l.fromScheduleName || l.fromScheduleCode}
                                          {(l.fromClassName || l.fromClassCode) && <> › {l.fromClassName || l.fromClassCode}</>}
                                        </>
                                      : <span style={{ color: 'var(--text-secondary)' }}>Unmapped</span>}
                                  </td>
                                  <td style={{ padding: '4px 6px', whiteSpace: 'nowrap' }}>
                                    {l.restored
                                      ? <span style={chipStyle('#dbeafe', '#1e40af')}>Restored</span>
                                      : l.outcome === 'SCHEDULE_ONLY'
                                        ? <span style={chipStyle('#fef3c7', '#92400e')}>Schedule-only</span>
                                        : <span style={chipStyle('#fee2e2', '#991b1b')}>Unmapped</span>}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </details>
                    </div>
                  )}

                  {(preview.statementsToRegenerate.length > 0 || preview.reportsToReview.length > 0) && (
                    <div style={calloutStyle('var(--bg-secondary)', 'var(--text-primary)')}>
                      <strong>Flagged for review after the switch</strong>
                      {preview.statementsToRegenerate.length > 0 && (
                        <span>
                          Statements to regenerate in the new layout: {preview.statementsToRegenerate.map(s => s.name || s.code).join(', ')}.
                        </span>
                      )}
                      {preview.reportsToReview.length > 0 && (
                        <span>
                          Reports whose wording changes to the new template's text (your answers are kept):{' '}
                          {preview.reportsToReview.map(r => r.name || r.code).join(', ')}.
                        </span>
                      )}
                    </div>
                  )}

                  {(preview.orphanedNotes.length > 0 || preview.orphanedReports.length > 0) && (
                    <div style={calloutStyle('#fef3c7', '#92400e')}>
                      <strong style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <IconSystem.alert size={14} /> Saved data the new template has no place for
                      </strong>
                      {preview.orphanedNotes.length > 0 && (
                        <span>Notes: {preview.orphanedNotes.map(n => n.name || n.code).join(', ')}.</span>
                      )}
                      {preview.orphanedReports.length > 0 && (
                        <span>Statements / reports: {preview.orphanedReports.map(n => n.name || n.code).join(', ')}.</span>
                      )}
                      <span>The data is kept but no longer shown. It reappears if you switch back.</span>
                    </div>
                  )}

                  {hasStructuralChanges ? (
                    <details style={{ marginTop: 10 }}>
                      <summary style={{ cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>Structure differences</summary>
                      <ChipSection title="Notes added" items={preview.schedulesAdded} bg="#dcfce7" fg="#166534" />
                      <ChipSection title="Notes removed" items={preview.schedulesRemoved} bg="#fee2e2" fg="#991b1b" />
                      <ChipSection title="Classes added" items={preview.classesAdded} bg="#dcfce7" fg="#166534" />
                      <ChipSection title="Classes removed" items={preview.classesRemoved} bg="#fee2e2" fg="#991b1b" />
                    </details>
                  ) : (
                    <div style={calloutStyle('#dcfce7', '#166534')}>
                      No notes or classes are added or removed — all saved data carries over 1:1.
                    </div>
                  )}

                  <div style={{ marginTop: 10, fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                    Amounts, workspaces and note numbering carry over unchanged where the note exists in both templates.
                    Every ledger's current mapping is recorded, so switching back later restores the ones this switch moves.
                  </div>
                </>
              )}
            </>
          )}
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={upgrading}>Cancel</button>
          <button className={`btn ${preview?.crossesEntityFamily ? 'btn-danger' : 'btn-primary'}`} onClick={() => setConfirmOpen(true)} disabled={!canUpgrade}>
            {upgrading ? 'Switching…' : ready ? (isSwitch ? `Switch to ${preview!.toTemplateName}` : `Upgrade to v${preview!.toVersion}`) : 'Switch'}
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        title={isSwitch ? 'Switch Template' : 'Upgrade Template Version'}
        message={preview
          ? `Move this engagement from ${preview.fromTemplateName} v${preview.fromVersion} to ${toLabel}? ` +
            (degraded > 0
              ? `${plural(preview.tbEntriesToScheduleOnly, 'ledger')} will move to schedule-only and ` +
                `${plural(preview.tbEntriesToUnclassify, 'ledger')} will become unmapped. `
              : 'All ledger mappings carry over. ') +
            (preview.tbEntriesRestorable > 0
              ? `${plural(preview.tbEntriesRestorable, 'ledger')} get their earlier mapping back. `
              : '') +
            (hasWarnings ? 'Review the flagged items after the switch.' : '')
          : ''}
        confirmLabel={isSwitch ? 'Switch' : 'Upgrade'}
        cancelLabel="Cancel"
        variant={hasWarnings ? 'danger' : 'default'}
        loading={upgrading}
        loadingMessage="Switching…"
        onConfirm={handleConfirmUpgrade}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
};

export default FsaTemplateUpgradeModal;
